// Synthesized UI sounds via Web Audio, so no audio assets need shipping.
// This module is the single owner of every app sound: features call the named
// sounds below rather than playing audio themselves.
import { usePersistStore } from '../store/persistentStorage'

// One lazily created context shared by all UI sounds; browsers cap the
// number of concurrent AudioContexts, so per-sound contexts are not an option.
let ctx: AudioContext | null = null

function ensureContext(): AudioContext {
    ctx ??= new AudioContext()
    // Browsers start contexts suspended until a user gesture has happened
    if (ctx.state === 'suspended') {
        ctx.resume().catch((e) => console.warn('[sound] Failed to resume audio context:', e))
    }
    return ctx
}

interface BeepOptions {
    frequencyHz: number
    offsetSec: number
    durationSec: number
    volume: number
    type?: OscillatorType
}

function beep(
    context: AudioContext,
    { frequencyHz, offsetSec, durationSec, volume, type = 'sine' }: BeepOptions
): void {
    const startTime = context.currentTime + offsetSec
    const oscillator = context.createOscillator()
    const gain = context.createGain()
    oscillator.connect(gain).connect(context.destination)

    oscillator.type = type
    oscillator.frequency.setValueAtTime(frequencyHz, startTime)

    gain.gain.setValueAtTime(volume, startTime)
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + durationSec)

    oscillator.start(startTime)
    oscillator.stop(startTime + durationSec)
}

// A UI sound must never throw into its caller; AudioContext creation can fail
// when the page hits the browser's concurrent-context limit.
function play(compose: (context: AudioContext) => void): void {
    if (!usePersistStore.getState().soundsEnabled) {
        return
    }
    try {
        compose(ensureContext())
    } catch (e) {
        console.warn('[sound] Failed to play sound:', e)
    }
}

export const sounds = {
    // Double beep, reused for every warning.
    warning(): void {
        play((context) => {
            beep(context, { frequencyHz: 660, offsetSec: 0, durationSec: 0.09, volume: 0.2 })
            beep(context, { frequencyHz: 660, offsetSec: 0.14, durationSec: 0.09, volume: 0.2 })
        })
    },
    // Two-note drop, reused for every error, kept distinct from the warning tone.
    error(): void {
        play((context) => {
            beep(context, { frequencyHz: 392, offsetSec: 0, durationSec: 0.1, volume: 0.25, type: 'triangle' })
            beep(context, { frequencyHz: 311, offsetSec: 0.14, durationSec: 0.18, volume: 0.25, type: 'triangle' })
        })
    },
    // Crisp blip when a recording starts.
    recordStart(): void {
        play((context) => beep(context, { frequencyHz: 880, offsetSec: 0, durationSec: 0.1, volume: 0.2 }))
    },
    // Low blip when a recording stops.
    recordStop(): void {
        play((context) => beep(context, { frequencyHz: 440, offsetSec: 0, durationSec: 0.12, volume: 0.2 }))
    },
}
